import React, { useRef, useState } from "react";
import parse from "html-react-parser";
import { useHeroDispatchContext } from "../../hero-container/hero-container";

const normalizeInfoSlides = (info) => {
  if (!Array.isArray(info)) return [];

  return info.map((slide) => {
    if (slide.type === "cards" || slide.type === "timeline") return slide;

    const descriptions = Array.isArray(slide.description) ? slide.description : [];
    const isTimeline =
      descriptions.length > 0 && descriptions.every((item) => item.subtitle && String(item.subtitle).trim());

    if (isTimeline) {
      return {
        title: slide.title,
        type: "timeline",
        heading: slide.title,
        points: descriptions.map((item, index) => ({
          label: item.subtitle,
          text: item.text,
          isCurrent: index === 0,
        })),
      };
    }

    return {
      title: slide.title,
      type: "cards",
      heading: slide.heading || slide.title,
      text: "",
      items: descriptions.map((item) => ({
        iconUrl: item.iconUrl,
        title: item.subtitle || "",
        text: item.text,
        linkText: item.linkText,
        linkUrl: item.linkUrl,
      })),
    };
  });
};

const InfoLink = ({ href, children }) => {
  const isExternal = /^https?:\/\//i.test(href);

  return (
    <a
      className="info-feature__link"
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
    >
      <span>
        {children}{" "}
        <span aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M4.16797 10H15.8346M10.8346 5L15.8346 10L10.8346 15"
              strokeWidth="1.25"
              strokeLinecap="round"
              strokeLinejoin="round"
              stroke="#698182"
            />
          </svg>
        </span>
      </span>
    </a>
  );
};

const InfoIcon = ({ iconUrl, className }) => {
  if (!iconUrl) return null;

  return (
    <span className={className}>
      <img src={iconUrl} alt="" />
    </span>
  );
};

const InfoCards = ({ slide, stackedIcon }) => {
  const items = Array.isArray(slide.items) ? slide.items : [];

  return (
    <div className="info-cards">
      {items.map((item, index) => (
        <article className={"info-feature" + (stackedIcon ? " info-feature--icon-row" : "")} key={item._id || index}>
          <InfoIcon iconUrl={item.iconUrl} className="info-feature__icon" />
          <div className="info-feature__body">
            {item.title ? <h3 className="info-feature__title">{item.title}</h3> : null}
            {item.text ? <div className="info-feature__text">{parse(item.text)}</div> : null}
          </div>
          {item.linkText && item.linkUrl ? <InfoLink href={item.linkUrl}>{item.linkText}</InfoLink> : null}
        </article>
      ))}
    </div>
  );
};

const InfoTimeline = ({ slide }) => {
  const points = Array.isArray(slide.points) ? slide.points : [];
  const currentIndex = points.findIndex((point) => point.isCurrent);
  const safeCurrentIndex = currentIndex < 0 ? 0 : currentIndex;
  const progress = points.length > 1 ? safeCurrentIndex / (points.length - 1) : 0;
  const banner = slide.banner;

  return (
    <div>
      <div className="info-timeline" style={{ "--progress": progress }}>
        <span className="info-timeline__line" />
        <div className="info-timeline__points">
          {points.map((point, index) => (
            <div
              className={"info-timeline__point" + (index <= safeCurrentIndex ? " is-reached" : "")}
              key={point._id || index}
            >
              <span className="info-timeline__dot" />
              <div className="info-timeline__copy">
                {point.label ? <p className="info-timeline__label">{point.label}</p> : null}
                {point.text ? <div className="info-timeline__text">{parse(point.text)}</div> : null}
              </div>
            </div>
          ))}
        </div>
      </div>
      {banner && (banner.text || banner.label) ? (
        <div className="info-banner">
          <InfoIcon iconUrl={banner.iconUrl} className="info-banner__icon" />
          <p className="info-banner__text">
            {banner.label ? <strong>{banner.label} </strong> : null}
            {banner.text ? parse(banner.text) : null}
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default function InfoTabpanel(props) {
  const setActiveTabId = useHeroDispatchContext();
  const slides = normalizeInfoSlides(props.content && props.content.info);
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const touchStartX = useRef(0);
  const safeIndex = slides.length && activeIndex >= slides.length ? 0 : activeIndex;
  const slide = slides[safeIndex];
  const gallery = props.content && props.content.gallery;
  const galleryImage = gallery && gallery.length ? gallery[0] : null;
  const backgroundImage =
    galleryImage &&
    (galleryImage.image1600Url || galleryImage.image2048Url || galleryImage.image800Url || galleryImage.image3840Url);

  const goTo = (nextIndex) => {
    if (!slides.length) return;
    const total = slides.length;
    const wrapped = (nextIndex + total) % total;
    if (wrapped === safeIndex) return;
    const forward = (wrapped - safeIndex + total) % total;
    const backward = (safeIndex - wrapped + total) % total;
    setDirection(forward <= backward ? 1 : -1);
    setActiveIndex(wrapped);
  };

  const onTouchStart = (event) => {
    touchStartX.current = event.changedTouches[0].clientX;
  };

  const onTouchEnd = (event) => {
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) < 40) return;
    goTo(delta < 0 ? safeIndex + 1 : safeIndex - 1);
  };

  if (!slide) return null;

  const heading = slide.heading || slide.title;

  return (
    <div className="hero-block-info">
      {backgroundImage ? <img className="hero-block-info__bg" src={backgroundImage} alt="" /> : null}
      <span className="hero-block-info__shade" />
      <article className="hero-info-card" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <button
          type="button"
          aria-label="Close info card"
          className="info-card__close-btn"
          onClick={props.onClose || (() => setActiveTabId && setActiveTabId("tour"))}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12.75 0.75L0.75 12.75M0.75 0.75L12.75 12.75"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              stroke="#698182"
            />
          </svg>
        </button>
        <div className="info-card__header">
          <div className="info-card__tabs" role="tablist">
            {slides.map((item, index) => (
              <button
                key={item._id || index}
                type="button"
                role="tab"
                aria-selected={index === safeIndex}
                className={"info-card__tab" + (index === safeIndex ? " is-active" : "")}
                onClick={() => goTo(index)}
              >
                {item.title}
              </button>
            ))}
          </div>
          <div className="info-card__nav">
            <button type="button" className="info-card__nav-btn" aria-label="Previous" onClick={() => goTo(safeIndex - 1)}>
              <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M6.75 0.75L0.75 6.75L6.75 12.75"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  stroke="#043B3B"
                />
              </svg>
            </button>
            <span className="info-card__count">
              <span>{safeIndex + 1}</span>
              <span> / {slides.length}</span>
            </span>
            <button type="button" className="info-card__nav-btn" aria-label="Next" onClick={() => goTo(safeIndex + 1)}>
              <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M0.75 0.75L6.75 6.75L0.75 12.75"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  stroke="#043B3B"
                />
              </svg>
            </button>
          </div>
        </div>

        <div key={safeIndex} className={"info-card__body info-card__body--" + (direction > 0 ? "next" : "prev")}>
          {heading ? <h2 className="info-card__heading">{heading}</h2> : null}
          {slide.type !== "timeline" && slide.text ? <div className="info-card__intro">{parse(slide.text)}</div> : null}
          {slide.type === "timeline" ? <InfoTimeline slide={slide} /> : <InfoCards slide={slide} stackedIcon={safeIndex === 2} />}
        </div>
      </article>
    </div>
  );
};
